/**
 * Treść strony /dla-firm — wydzielona z pages/dla-firm.tsx jako osobny,
 * JSON-serializable config (string/number, żadnych referencji do
 * komponentów — `iconKey` to string mapowany na komponent Lucide wewnątrz
 * dla-firm.tsx, ten sam wzorzec co CategoryNav.tsx/Footer.tsx).
 */
export interface ClientCard {
  iconKey: string;
  title: string;
  desc: string;
}

export interface OfferCard {
  iconKey: string;
  title: string;
  desc: string;
  tag: string;
}

export interface WhyCard {
  title: string;
  desc: string;
}

export interface StepCard {
  num: string;
  title: string;
  desc: string;
}

export const DLA_FIRM_CONTENT = {
  hero: {
    tagline: 'Kwiaty, które tworzą atmosferę.',
    headingLine1: 'Kwiaty dla firm',
    headingAccent: 'w Lublinie',
    body: 'Kompozycje do biura i lokalu, kwiaty na wydarzenie oraz prezenty dla pracowników i partnerów. Opisz swoją potrzebę, a ustalimy zakres zamówienia i sposób realizacji.',
    ctaPhoneLabel: 'Skontaktuj się z nami',
    ctaOfferLabel: 'Zapytaj o ofertę',
  },
  forWho: {
    eyebrow: 'Odbiorcy',
    heading: 'Kwiaty do biur, lokali i przestrzeni spotkań',
    clients: [
      { iconKey: 'building2', title: 'Biura i przestrzenie pracy', desc: 'Kompozycja na recepcję, do sali spotkań lub gabinetu. Przy wyborze uwzględnij miejsce ustawienia i codzienne korzystanie z przestrzeni.' },
      { iconKey: 'utensils',  title: 'Restauracje i hotele', desc: 'Eleganckie kompozycje do stołów, recepcji i przestrzeni wspólnych.' },
      { iconKey: 'sparkles',  title: 'Salony kosmetyczne i fryzjerskie', desc: 'Kwiaty do strefy powitania i poczekalni. Kolorystykę warto zestawić z wystrojem oraz detalami identyfikacji salonu.' },
      { iconKey: 'store',     title: 'Sklepy i lokale usługowe', desc: 'Kompozycje przyciągające wzrok i tworzące przyjazne otoczenie.' },
      { iconKey: 'calendar',  title: 'Firmy organizujące wydarzenia', desc: 'Oprawa florystyczna konferencji, otwarć, jubileuszy i innych spotkań.' },
      { iconKey: 'home',      title: 'Deweloperzy i przestrzenie pokazowe', desc: 'Kwiaty, które ożywiają showroomy i nadają im wyjątkowy charakter.' },
    ] satisfies ClientCard[],
  },
  offer: {
    eyebrow: 'Oferta',
    heading: 'Dekoracja miejsca czy kwiatowy prezent od firmy?',
    items: [
      { iconKey: 'leaf',    title: 'Dekoracje kwiatowe do przestrzeni', desc: 'Świeże kompozycje do biur, recepcji, restauracji, hoteli i innych przestrzeni. Kwiaty dobrane do charakteru miejsca i wystroju.', tag: 'Przestrzeń' },
      { iconKey: 'calendar',title: 'Kwiaty na wydarzenia firmowe', desc: 'Oprawa florystyczna spotkań, konferencji, otwarć, jubileuszy i innych wydarzeń. Dekoracje kwiatowe do przestrzeni w Lublinie i okolicach.', tag: 'Eventy' },
      { iconKey: 'flower',  title: 'Kwiaty dla klientów i partnerów', desc: 'Bukiet z podziękowaniem za współpracę, na otwarcie lokalu lub jubileusz partnera. Wybierz styl odpowiedni do okazji i dołącz krótki podpis firmy.', tag: 'Relacje' },
      { iconKey: 'gift',    title: 'Kwiaty jako firmowy prezent', desc: 'Możliwość zamówienia kwiatów dla pracowników, klientów lub kontrahentów - indywidualnie lub w większych ilościach.', tag: 'Prezenty' },
      { iconKey: 'refresh', title: 'Regularne dostawy kwiatów', desc: 'Cykliczne kompozycje do firmy w Lublinie i okolicach. Częstotliwość, budżet i sposób odbioru ustalamy przed rozpoczęciem współpracy.', tag: 'Cyklicznie' },
    ] satisfies OfferCard[],
  },
  regularCare: {
    eyebrow: 'Współpraca długoterminowa',
    headingLine1: 'Kwiaty w Twojej przestrzeni',
    headingAccent: 'przez cały rok',
    body: 'Przy stałej współpracy określ, w których miejscach mają pojawiać się kwiaty i kto będzie je odbierać. Zdjęcie recepcji lub stołu oraz orientacyjne wymiary pomogą dobrać proporcje kompozycji. Porozmawiamy o częstotliwości zamówień, kolorystyce i budżecie. Warto też uzgodnić, kto zajmuje się kwiatami pomiędzy dostawami oraz jak zgłaszać zmianę planu przy urlopie lub zamknięciu lokalu.',
    ctaLabel: 'Porozmawiajmy o współpracy',
    checklist: [
      'Regularne dostawy świeżych kwiatów do firmy',
      'Kompozycje dobrane do charakteru przestrzeni',
      'Elastyczny harmonogram - tygodniowy, dwutygodniowy, miesięczny',
      'Stały kontakt i możliwość modyfikacji zamówień',
      'Realizacja w Lublinie i okolicach',
    ] as string[],
  },
  why: {
    eyebrow: 'O nas',
    heading: 'Co ustalimy przed realizacją?',
    items: [
      { title: 'Styl i miejsce ustawienia', desc: 'Spokojna kompozycja do sali rozmów czy wyraźny akcent przy wejściu? Ustalimy kolorystykę, formę i proporcje pasujące do miejsca.' },
      { title: 'Zakres i budżet', desc: 'Podaj liczbę kompozycji i orientacyjny budżet. Przy wydarzeniu rozróżnij dekoracje stołów, recepcji i kwiaty przeznaczone do wręczenia.' },
      { title: 'Jednorazowo lub cyklicznie', desc: 'Wybierz zamówienie na konkretne wydarzenie albo porozmawiaj o powtarzających się dostawach. Szczegóły współpracy potwierdzimy indywidualnie.' },
      { title: 'Przekazanie kwiatów', desc: 'Uzgodnimy adres w Lublinie lub okolicy, termin oraz kontakt do osoby odbierającej. Przy wydarzeniu podaj także godzinę rozpoczęcia.' },
    ] satisfies WhyCard[],
  },
  howToStart: {
    eyebrow: 'Proces',
    heading: 'Jak rozpocząć współpracę?',
    steps: [
      { num: '01', title: 'Opisz zamówienie', desc: 'Podaj datę, adres, liczbę kompozycji i budżet. Napisz, czy chodzi o dekorację miejsca, prezenty dla kilku osób czy stałą współpracę.' },
      { num: '02', title: 'Ustalamy szczegóły', desc: 'Dobierzemy rozwiązanie, zakres usług i częstotliwość współpracy dopasowaną do Twoich potrzeb.' },
      { num: '03', title: 'Zaczynamy działać', desc: 'Przygotujemy kwiaty i zajmiemy się ich dostarczeniem do Lublina lub okolic zgodnie z ustaleniami.' },
    ] satisfies StepCard[],
  },
  cta: {
    eyebrow: 'Kontakt',
    headingLine1: 'Porozmawiajmy o kwiatach',
    headingAccent: 'dla Twojej firmy',
    body: 'Prześlij informacje o okazji i miejscu realizacji. Jeśli wybierasz kwiaty dla kilku odbiorców, zaznacz, czy mają tworzyć jednakowe zestawy, czy różnić się stylem. Na tej podstawie omówimy propozycję dla Twojej firmy.',
    ctaLabel: 'Skontaktuj się z nami',
  },
};
